import { cleanupFile, uploadFileToGateway } from "@/api/chat";
import {
  exceedsUploadLimit,
  formatMaxSize,
  getUploadTicket,
} from "@/api/upload";
import { getFileExtension } from "@/lib/utils";
import type { FileAttachment } from "@/types";
import { useCallback, useRef, useState } from "react";
import {
  describeFailedUploads as describeFailedUploadsShared,
  planSend,
} from "./attachmentSend";
export interface PendingFile {
  file: File;
  uploading: boolean;
  uploadedUrl?: string;
  error?: string;
}
function describeFailedUploads(files: PendingFile[]): string {
  return describeFailedUploadsShared(
    files.map((f) => ({ fileName: f.file.name, error: f.error })),
  );
}
const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/webp",
];
const validateFile = (file: File): string | null => {
  if (/\.xls$/i.test(file.name))
    return "Save XLS workbooks as XLSX before uploading.";
  if (
    !ALLOWED_TYPES.includes(file.type) &&
    !file.name.match(/\.(pdf|xlsx|xls|csv|docx|png|jpg|jpeg|webp)$/i)
  ) {
    return "Unsupported file type";
  }
  return null;
};
export function useChatAttachments(token: string | null) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const updateFile = useCallback(
    (index: number, updates: Partial<PendingFile>) => {
      setPendingFiles((prev) =>
        prev.map((f, i) => (i === index ? { ...f, ...updates } : f)),
      );
    },
    [],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const resolveMaxSize = useCallback(async () => {
    try {
      const ticket = await getUploadTicket(
        new File([], "probe.pdf"),
        "evidence",
        token ?? undefined,
      );
      return ticket.maxSizeBytes;
    } catch {
      // Offline or the service is down. Returning undefined means the size check
      // is skipped and the upload is attempted, so the real error surfaces there
      // instead of the file being rejected for a reason we could not determine.
      return undefined;
    }
  }, [token]);
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (pendingFiles.length + files.length > 3) {
      return;
    }
    const typeErrors = files.map(validateFile);
    const maxSizeBytes = typeErrors.some((error) => !error)
      ? await resolveMaxSize()
      : undefined;
    const newFiles: PendingFile[] = files.map((file, index) => {
      const error =
        typeErrors[index] ??
        (maxSizeBytes !== undefined && exceedsUploadLimit(file, maxSizeBytes)
          ? `File exceeds the ${formatMaxSize(maxSizeBytes)} limit`
          : null);
      return error
        ? { file, uploading: false, error }
        : { file, uploading: false };
    });
    setPendingFiles((prev) => [...prev, ...newFiles]);
    setUploadError(null);
  };
  const removeFile = useCallback(
    (index: number) => {
      const f = pendingFiles[index];
      if (f.uploadedUrl && token) {
        cleanupFile(f.uploadedUrl, token).catch(() => {});
      }
      setPendingFiles((prev) => prev.filter((_, i) => i !== index));
      setUploadError(null);
    },
    [pendingFiles, token],
  );
  const prepareAttachments = useCallback(
    async (onStart: () => void): Promise<FileAttachment[] | null> => {
      const alreadyFailed = pendingFiles.filter((f) => f.error);
      if (alreadyFailed.length > 0) {
        setUploadError(
          `${describeFailedUploads(alreadyFailed)}. Remove it or reattach the file before sending.`,
        );
        return null;
      }
      setUploadError(null);
      onStart();
      setUploading(true);
      const plan = planSend(
        pendingFiles.map((f) => ({
          fileName: f.file.name,
          uploadedUrl: f.uploadedUrl,
          error: f.error,
        })),
      );
      const uploaded: FileAttachment[] = plan.alreadyUploaded.map((f) => ({
        fileName: f.fileName,
        s3Url: f.uploadedUrl!,
        fileType: getFileExtension(f.fileName),
      }));
      const uploadTargets = pendingFiles
        .map((f, index) => ({ f, index }))
        .filter(({ f }) => !f.uploadedUrl);
      const failedThisAttempt: PendingFile[] = [];
      for (const { f, index } of uploadTargets) {
        updateFile(index, { uploading: true });
        try {
          const s3Url = await uploadFileToGateway(f.file, token!);
          updateFile(index, { uploading: false, uploadedUrl: s3Url });
          uploaded.push({
            fileName: f.file.name,
            s3Url,
            fileType: getFileExtension(f.file.name),
          });
        } catch (error) {
          // Surface the service's reason (over the size limit, wrong type, S3
          // refused) instead of collapsing every cause into "Upload failed", which
          // leaves the user with nothing to act on.
          const reason =
            error instanceof Error && error.message
              ? error.message
              : "Upload failed";
          updateFile(index, { uploading: false, error: reason });
          failedThisAttempt.push({ ...f, uploading: false, error: reason });
        }
      }
      setUploading(false);
      const failed = [
        ...pendingFiles.filter((f) => !f.uploadedUrl && f.error),
        ...failedThisAttempt,
      ];
      if (failed.length > 0) {
        setUploadError(
          `${describeFailedUploads(failed)}. Nothing was sent — remove the file or try again.`,
        );
        return null;
      }
      return uploaded;
    },
    [pendingFiles, token, updateFile],
  );
  const clearAttachments = () => {
    setPendingFiles([]);
    setUploadError(null);
  };
  return {
    pendingFiles,
    uploading,
    uploadError,
    setUploadError,
    fileInputRef,
    imageInputRef,
    handleFileSelect,
    removeFile,
    prepareAttachments,
    clearAttachments,
  };
}
